import React, { useState, useEffect, useContext, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { firestore, auth } from "./firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import "./App.css";
import { AuthContext } from "./AuthContext";
import ErrorBoundary from "./ErrorBoundary";
import FilterPrototype from "./FilterPrototype";


// Lazy-load components
const MainLayout = React.lazy(() => import("./MainLayout"));
const PersistentLayout = React.lazy(() => import("./PersistentLayout"));
const Player = React.lazy(() => import("./Player"));
const Admin = React.lazy(() => import("./Admin"));
const EditProfile = React.lazy(() => import("./EditProfile"));
const CuratorProfile = React.lazy(() => import("./CuratorProfile"));
const ManageTracks = React.lazy(() => import("./ManageTracks"));
const HowIWork = React.lazy(() => import("./HowIWork"));
const ManageArtists = React.lazy(() => import("./ManageArtists"));
const ManageLabels = React.lazy(() => import("./ManageLabels"));
const UploadPanel = React.lazy(() => import("./UploadPanel")); // ⬅️ new
const AdminDashboard = React.lazy(() => import("./AdminDashboard")); // ⬅️ new
const InvitePanel = React.lazy(() => import("./InvitePanel"));
const AcceptInvite = React.lazy(() => import("./AcceptInvite"));
const Deeper = React.lazy(() => import("./Deeper"));
const Manifesto = React.lazy(() => import("./Manifesto"));

function AdminPage({ currentUser, handleLogin, email, password, setEmail, setPassword, loginError, setLoginError }) {
  if (currentUser) {
    return <Admin currentUser={currentUser} />;
  }
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        padding: "0 20px",
      }}
    >
      <label htmlFor="login-email" className="visually-hidden">Email</label>
      <input
        id="login-email"
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (loginError) setLoginError("");
        }}
        style={{ padding: "4px", margin: "5px" }}
        autoComplete="email"
      />
      <label htmlFor="login-password" className="visually-hidden">Password</label>
      <input
        id="login-password"
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          if (loginError) setLoginError("");
        }}
        style={{ padding: "4px", margin: "5px" }}
        autoComplete="current-password"
      />
      <button onClick={handleLogin} style={{ padding: "4px 8px", margin: "5px" }}>
        Login
      </button>
      {loginError && (
        <p style={{ color: "#c00", fontSize: "13px", marginTop: "10px", maxWidth: "250px" }}>
          {loginError}
        </p>
      )}
    </div>
  );
}

function PersistentPlayerWrapper({ radioStarted, setRadioStarted }) {
  const location = useLocation();
  const isHome = location.pathname === "/";
  return (
    <div className="persistent-player-wrapper">
      <Suspense fallback={null}>
        <Player liveMode={isHome} radioStarted={radioStarted} setRadioStarted={setRadioStarted} />
      </Suspense>
    </div>
  );
}

function App() {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isHome = location.pathname === "/";
  const isHowIWork = location.pathname === "/how-i-work";

  useEffect(() => {
    if (user) {
      ensureUserDocExists(user);
    }
  }, [user]);

  async function ensureUserDocExists(user) {
    const userRef = doc(firestore, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        displayName: user.displayName || "",
        email: user.email || "",
        createdAt: serverTimestamp(),
      });
      console.log("✅ Created user profile doc for:", user.uid);
    }
  }

  const [radioStarted, setRadioStarted] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const handleLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setLoginError("");
    } catch (error) {
      // Show specific error messages based on Firebase error codes
      const errorMessages = {
        "auth/wrong-password": "Wrong password. Please try again.",
        "auth/user-not-found": "No account found with that email.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/invalid-credential": "Incorrect email or password. Please try again.",
        "auth/invalid-login-credentials": "Incorrect email or password. Please try again.",
        "auth/too-many-requests": "Too many failed attempts. Please wait a moment and try again.",
        "auth/network-request-failed": "Network error. Check your connection and try again.",
      };
      const message = errorMessages[error.code] || "Login failed. Please try again.";
      setLoginError(message);
    }
  };

  return (
    <>
      <PersistentPlayerWrapper radioStarted={radioStarted} setRadioStarted={setRadioStarted} />

      <ErrorBoundary>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<MainLayout />} />

            {/* Admin Dashboard (after login) */}
<Route
  path="/admin"
  element={
    user ? (
      <AdminDashboard currentUser={user} />
    ) : (
      <AdminPage
        currentUser={user}
        handleLogin={handleLogin}
        email={email}
        password={password}
        setEmail={setEmail}
        setPassword={setPassword}
        loginError={loginError}
        setLoginError={setLoginError}
      />
    )
  }
/>

{/* Upload Panel under its own route */}
<Route
  path="/admin/upload"
  element={user ? <UploadPanel currentUser={user} /> : <Navigate to="/" />}
/>

            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/curators/:curatorId" element={<CuratorProfile />} />
            <Route path="/manage-tracks" element={user ? <ManageTracks currentUser={user} /> : <Navigate to="/" />} />
            <Route path="/manage-artists" element={user ? <ManageArtists currentUser={user} /> : <Navigate to="/" />} />
            <Route path="/manage-labels" element={user ? <ManageLabels currentUser={user} /> : <Navigate to="/" />} />

            <Route
  path="/admin/invites"
  element={user ? <InvitePanel currentUser={user} /> : <Navigate to="/" />}
/>

            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/manifesto" element={user ? <Manifesto /> : <Navigate to="/" />} />

            <Route
              path="/how-i-work"
              element={
                <PersistentLayout>
                  <HowIWork />
                </PersistentLayout>
              }
            />

            <Route path="/filters" element={<FilterPrototype />} />
            <Route path="/deeper" element={<Deeper />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      <footer
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 20px",
          zIndex: 1001,
          fontFamily: "Georgia, serif",
          fontSize: "13px",
        }}
      >
        {isHome ? (
          <Link
            style={{
              textDecoration: "none",
              color: "black",
              opacity: 0.4,
            }}
            to="/deeper"
          >
            Deeper
          </Link>
        ) : (
          <Link
            style={{
              textDecoration: "none",
              color: "black",
              opacity: 0.4,
            }}
            to="/"
          >
            Home
          </Link>
        )}
        {isHowIWork ? (
          <Link
            style={{
              textDecoration: "none",
              color: "black",
              opacity: 0.4,
            }}
            to="/deeper"
          >
            Deeper
          </Link>
        ) : (
          <Link
            style={{
              textDecoration: "none",
              color: "black",
              opacity: 0.4,
            }}
            to="/how-i-work"
          >
            How I Work
          </Link>
        )}
      </footer>
    </>
  );
}

export default App;
